<template>
    <div>
        <kgr-alert sticky />
        <kgr-note-box
            html
            text-key="staffEvaluation.note.text"
            title-key="staffEvaluation.note.title"
        />
        <br />
        <kgr-form-scaffold
            :pending="loading"
            :with-back-button="false"
            :with-primary-buttons="true"
            with-print-button
            print-view-hide-buttons
            :save-button-label="$t('save')"
            :save-button-disabled="!formIsValid"
            @primaryBtnClicked="save"
        >
            <table class="kgr-table kgr-table--block kgr-table--striped table-groups kgr-row roles">
                <thead>
                    <tr class="kgr-table__row">
                        <th
                            v-if="federationId !== null"
                            v-t="'Einrichtungsname'"
                            class="kgr-table__cell kgr-table__cell--headline"
                        ></th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Soll-Wochenstunden*') }}
                            <kgr-icon-info
                                :title="$t('Soll-Wochenstunden der Fachkräfte und Leitung')"
                                accent
                                small
                                class="info-icon"
                            />
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Ist-Wochenstunden') }}
                            <kgr-icon-info
                                :title="$t('Aktuelle (heute) Ist-Wochenstunden der tatsächlich verfügbaren Fachkräfte und Leitung')"
                                accent
                                small
                                class="info-icon"
                            />
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Differenz') }}
                            <kgr-icon-info
                                :title="$t('Differenz der Soll-Wochenstunden und der vorherigen Spalte')"
                                accent
                                small
                                class="info-icon"
                            />
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Ist-Wochenstunden am {date}', {date: firstDayOfNextMonth.toString()}) }}
                            <kgr-icon-info
                                :title="$t('Ist-Wochenstunden der tatsächlich verfügbaren Fachkräfte und Leitung am ersten Tag des Folgemonats')"
                                accent
                                small
                                class="info-icon"
                            />
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Differenz') }}
                            <kgr-icon-info
                                :title="$t('Differenz der Soll-Wochenstunden und der vorherigen Spalte')"
                                accent
                                small
                                class="info-icon"
                            />
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('staffEvaluation.column.actualHoursAtDate') }}
                            <kgr-date-picker
                                v-model="evaluationAt"
                                class="inlineDatePicker"
                            />
                            <kgr-icon-info
                                :title="$t('Ist-Wochenstunden der tatsächlich verfügbaren Fachkräfte und Leitung an einem frei wählbaren Stichtag')"
                                accent
                                small
                                class="info-icon"
                            />
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Differenz') }}
                            <kgr-icon-info
                                :title="$t('Differenz der Soll-Wochenstunden und der vorherigen Spalte')"
                                accent
                                small
                                class="info-icon"
                            />
                        </th>
                    </tr>
                </thead>
                <tbody v-if="loading">
                    <tr class="kgr-table__row">
                        <td
                            class="kgr-table__cell"
                            colspan="6"
                        >
                            <kgr-loading />
                        </td>
                    </tr>
                </tbody>
                <tbody v-else>
                    <tr
                        v-for="entry in entries"
                        :key="entry.kindergarten.id"
                        class="kgr-table__row"
                    >
                        <td
                            v-if="federationId !== null"
                            class="kgr-table__cell"
                        >
                            <a
                                :href="loginUrl(entry.kindergarten.id)"
                                class="kgr-link kgr-link--unobtrusive"
                            >{{ entry.kindergarten.name }}</a>
                        </td>
                        <td class="kgr-table__cell">
                            <kgr-number-input v-model="entry.theoreticalHoursPerWeek" />
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--rightAligned">
                            {{ entry.getActualHoursPerWeek(today.toIso8601()) }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--rightAligned">
                            <span
                                :class="getDeviationClass(entry, today.toIso8601())"
                            >
                                {{ getDeviationValue(entry, today.toIso8601()) }}{{ $t('staffEvaluation.config.thresholdUnit.' + entry.config.thresholdUnit) }}
                            </span>

                            <kgr-icon-info
                                :title="$t('staffEvaluation.config.threshold.info.' + entry.config.thresholdUnit, {thresholdDeviation: entry.config.thresholdDeviation, thresholdSevereDeviation: entry.config.thresholdSevereDeviation, thresholdNegativeDeviation: entry.config.thresholdNegativeDeviation})"
                                accent
                                small
                                class="info-icon"
                            />
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--rightAligned">
                            {{ entry.getActualHoursPerWeek(firstDayOfNextMonth.toIso8601()) }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--rightAligned">
                            <span
                                :class="getDeviationClass(entry,firstDayOfNextMonth.toIso8601())"
                            >
                                {{ getDeviationValue(entry,firstDayOfNextMonth.toIso8601()) }}{{ $t('staffEvaluation.config.thresholdUnit.' + entry.config.thresholdUnit) }}
                            </span>

                            <kgr-icon-info
                                :title="$t('staffEvaluation.config.threshold.info.' + entry.config.thresholdUnit, {thresholdDeviation: entry.config.thresholdDeviation, thresholdSevereDeviation: entry.config.thresholdSevereDeviation, thresholdNegativeDeviation: entry.config.thresholdNegativeDeviation})"
                                accent
                                small
                                class="info-icon"
                            />
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--rightAligned">
                            {{ entry.getActualHoursPerWeek(evaluationAt.toIso8601()) }}
                        </td>
                        <td class="kgr-table__cell kgr-table__cell--rightAligned">
                            <span
                                :class="getDeviationClass(entry,evaluationAt.toIso8601())"
                            >
                                {{ getDeviationValue(entry,evaluationAt.toIso8601()) }}{{ $t('staffEvaluation.config.thresholdUnit.' + entry.config.thresholdUnit) }}
                            </span>

                            <kgr-icon-info
                                :title="$t('staffEvaluation.config.threshold.info.' + entry.config.thresholdUnit, {thresholdDeviation: entry.config.thresholdDeviation, thresholdSevereDeviation: entry.config.thresholdSevereDeviation, thresholdNegativeDeviation: entry.config.thresholdNegativeDeviation})"
                                accent
                                small
                                class="info-icon"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </kgr-form-scaffold>
    </div>
</template>

<script>
import Date from '../../js/date/Date';
import DatePicker from '../common/form/picker/DateObjectPicker.vue';
import FormScaffold from '../common/form/FormScaffold.vue';
import IconInfo from '../common/icon/IconInfo';
import Loading from '../common/Loading';
import NoteBox from '../common/NoteBox.vue';
import NumberInput from '../common/form/input/NumberInput.vue';
import StaffEvaluation from '../../js/dto/StaffEvaluation';
import axios from 'axios';
import moment from 'moment';

export default {
    components: {
        'kgr-date-picker': DatePicker,
        'kgr-loading': Loading,
        'kgr-icon-info': IconInfo,
        'kgr-number-input': NumberInput,
        'kgr-note-box': NoteBox,
        'kgr-form-scaffold': FormScaffold,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: false,
            default: null,
        },
        federationId: {
            type: Number,
            required: false,
            default: null,
        }
    },
    data() {
        return {
            loading: true,
            dateFrom: null,
            dateTo: null,
            today: Date.today(),
            evaluationAt: Date.fromIso8601(moment().startOf('month').add(2, 'M').format('YYYY-MM-DD')),
            firstDayOfNextMonth: Date.fromIso8601(moment().startOf('month').add(1, 'M').format('YYYY-MM-DD')),
            entries: [],
            thresholdUnits: [
                {value: 'percent', label: this.$t('staffEvaluation.config.thresholdUnit.percent')},
                {value: 'hours', label: this.$t('staffEvaluation.config.thresholdUnit.hours')},
            ],
            editConfigs: [],
        }
    },
    computed: {
        formIsValid() {
            return this.entries.filter(entry => entry.theoreticalHoursPerWeek === null).length === 0;
        }
    },
    watch: {
        evaluationAt: function(date) {
            if(date !== null) {
                this.load()
            }
        },
    },
    mounted()  {
        if(this.kindergartenId === null && this.federationId === null) {
            console.error('Either kindergartenId or federationId must be set.');
            return;
        }
        this.load();
    },
    methods: {
        save() {
            this.loading = true;

            const calls = this.entries.map(entry => {
                return axios.put('/kindergartens/' + entry.kindergarten.id + '/staff-evaluation/config', entry.serialize());
            });

            this.loading = true;
            axios.all(calls)
                .then(() => {
                    this.load();
                })
                .catch(() => {
                    this.$notify.error('error.loadingData');
                });
        },
        load() {
            this.loading = true;
            let url = this.kindergartenId ? '/kindergartens/' + this.kindergartenId + '/staff-evaluation'
                : '/federations/' + this.federationId + '/staff-evaluation';
            url += '/' + this.evaluationAt.toIso8601();
            axios.get(url).then(
                response => {
                    let entries = this.kindergartenId ? [response.data.data] : response.data.data;
                    this.entries = entries.map(data => StaffEvaluation.fromData(data));
                    this.loading = false;
                }
            ).catch(() => {
                this.$notify.error('error.loadingData');
                this.loading = false;
            });
        },
        getDeviationValue(entry, date) {
            if(entry.theoreticalHoursPerWeek === 0 || entry.theoreticalHoursPerWeek === null ) {
                return '-';
            }
            let diffHours = entry.getDiffHoursPerWeek(date)
            return parseFloat(entry.config.thresholdUnit === 'percent' ? diffHours / entry.theoreticalHoursPerWeek * 100 : diffHours, 2).toFixed(2);
        },
        getDeviationClass(entry, date) {
            let deviation = this.getDeviationValue(entry, date);
            if(deviation >= entry.config.thresholdSevereDeviation) {
                return 'severe-deviation';
            }
            if(deviation >= entry.config.thresholdDeviation) {
                return 'deviation';
            }
            if(deviation < -entry.config.thresholdNegativeDeviation) {
                return 'negative-deviation';
            }

            return 'no-deviation';
        },
        loginUrl(id) {
            return `/federation/login/kindergarten/${id}`;
        },
    }
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
    th .info-icon, td .info-icon {
        vertical-align: middle;
        display:inline
    }
    th .inlineDatePicker {
        width: 90px !important;
        display: inline-block !important;
    }
    span.negative-deviation {
        font-weight: var(--kgr-font-weight-bold);
        color: black !important;
    }
    span.deviation {
        font-weight: var(--kgr-font-weight-bold);
        color: orange !important;
    }
    span.severe-deviation {
        font-weight: var(--kgr-font-weight-bold);
        color: red !important;
    }
    span.no-deviation {
        color: green;
    }
</style>
